import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteFooter, SiteHeader } from "@/components/home/HomePage";
import { AgentDartWalkthroughSection } from "@/components/marketing/agentdart-walkthrough";
import "@/styles/home.css";
import "@/styles/agentdart.css";
import "@/styles/agentdart-walkthrough.css";

const TITLE = "AgentDart: an AI agent that works your capital raise | CapitalDart";
const DESCRIPTION =
  "AgentDart reads replies, decides what to do next, writes the follow-up, sends it when you allow, and books the meeting. Every action is logged.";
const URL = "https://capitaldart.com/agentdart";

const FAQS = [
  {
    question: "Does AgentDart send email by itself?",
    answer:
      "Only reply-driven follow-ups, only with permission to send on, only to contacts who replied with interest or confirmed a time we proposed, and under a daily limit. Everything else is a draft for you.",
  },
  {
    question: "Does it read my email?",
    answer:
      "It reads replies to CapitalDart outreach only, stores a short snippet, never the body, and you can disconnect at any time.",
  },
  {
    question: "Which calendars does it work with?",
    answer:
      "Zoom personal link or any link you paste, and Teams meetings created on your Outlook calendar. Google Meet is next.",
  },
];

const PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${URL}#webpage`,
      url: URL,
      name: TITLE,
      description: DESCRIPTION,
      isPartOf: { "@type": "WebSite", name: "CapitalDart", url: "https://capitaldart.com/" },
    },
    {
      "@type": "FAQPage",
      "@id": `${URL}#faq`,
      mainEntity: FAQS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
};

export const Route = createFileRoute("/agentdart")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(PAGE_SCHEMA) }],
  }),
  component: AgentDartPage,
});

const COLUMNS: { title: string; lines: string[] }[] = [
  {
    title: "Does on its own",
    lines: [
      "Checks every four hours and right after you set a goal.",
      "Reads replies and classifies them: interested, meeting requested, referral, not now, passed, out of office, bounce or unsubscribe.",
      "Updates contact status.",
      "Captures proposed meeting times.",
      "Writes follow-ups in your voice with your signature.",
      "Sends reply-driven follow-ups when you allow direct sending.",
      "Attaches the calendar invite with your Zoom link, or books a Teams meeting from your Outlook calendar.",
    ],
  },
  {
    title: "Always waits for you",
    lines: [
      "First outreach to a new contact.",
      "Follow-ups after silence.",
      "Replies that propose a new meeting time.",
      "Referrals.",
      "Anything past the daily sending limit.",
      "Any contact marked do not send, passed or bounced.",
    ],
  },
  {
    title: "You control",
    lines: [
      "The goal and the deadline.",
      "Drafts only or permission to send.",
      "The daily limit: 5 per deal.",
      "Pause and Resume.",
      "The mailbox it sends from: Outlook or Gmail.",
      "Every draft before it goes out in drafts mode.",
    ],
  },
];

const GOALS: { example: string; description: string }[] = [
  {
    example: "5 investors at Interested by one month from today.",
    description:
      "A funnel target. AgentDart works outreach, replies and meetings toward the count.",
  },
  {
    example: "Get through the 30-day plan.",
    description: "Works the checklist and reports what is done.",
  },
  {
    example: "Find warm paths into these 12 firms.",
    description: "Finds the founders and partners who can introduce you.",
  },
  {
    example: "Two LinkedIn posts a week.",
    description: "Drafts them on schedule for you to publish.",
  },
  {
    example: "Something else, in a sentence.",
    description: "Describe it; AgentDart proposes the actions and tracks them.",
  },
];

const SETUP_STEPS: { number: string; title: string; description: string }[] = [
  {
    number: "01",
    title: "Turn on AgentDart.",
    description: "On the deal's AgentDart tab. It starts checking every four hours.",
  },
  {
    number: "02",
    title: "Choose how you sign.",
    description: "Your name, title and signature line, so every draft reads as you.",
  },
  {
    number: "03",
    title: "Connect your mailbox.",
    description: "Outlook or Gmail. It reads replies to CapitalDart outreach only.",
  },
  {
    number: "04",
    title: "Set your meeting room.",
    description:
      "Zoom link, a booking page, or Teams and Google Meet booked straight onto your calendar.",
  },
  {
    number: "05",
    title: "Set permission and limits.",
    description: "Drafts only or permission to send, and the daily limit per deal.",
  },
];

function AgentDartPage() {
  return (
    <div className="cd-home">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">
        <section className="ad-hero">
          <div className="shell">
            <p className="eyebrow">AGENTDART</p>
            <h1>Your own investment banker. 24/7.</h1>
            <p>
              Set a goal for the deal. AgentDart checks every four hours, reads replies, books
              meetings, writes in your voice, and sends within the limits you set. You see every
              decision.
            </p>
            <div className="ad-hero-actions">
              <Link
                className="button button-gold button-hero"
                to="/auth"
                search={{ mode: "signup" as const }}
                data-cta="agentdart-hero"
              >
                Start my free trial <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>

        <AgentDartWalkthroughSection />

        <section id="agentdart-goals" className="paper section">
          <div className="shell">
            <p className="eyebrow">GOALS YOU CAN SET</p>
            <h2>Give it a goal and a deadline.</h2>
            <div className="ad-goals-grid">
              {GOALS.map((goal) => (
                <article className="ad-goal" key={goal.example}>
                  <p className="ad-goal-example">"{goal.example}"</p>
                  <p>{goal.description}</p>
                </article>
              ))}
            </div>
            <p className="ad-goals-closing">
              Every goal has a deadline. AgentDart tells you whether it is ahead, on pace, or
              behind.
            </p>
          </div>
        </section>
        <section id="agentdart-setup" className="section">
          <div className="shell">
            <p className="eyebrow">SETUP</p>
            <h2>Five steps and it is working.</h2>
            <div className="ad-setup-grid">
              {SETUP_STEPS.map((step) => (
                <article className="ad-setup-step" key={step.number}>
                  <div className="ad-setup-header">
                    <span className="ad-setup-num">{step.number}</span>
                    <strong>{step.title}</strong>
                  </div>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
            <p className="ad-setup-closing">
              The setup checklist opens on the AgentDart tab and tracks what is done.
            </p>
          </div>
        </section>

        <section className="paper section">
          <div className="shell">
            <p className="eyebrow">THE HANDOFF</p>
            <h2>What it does on its own, and what it waits for</h2>
            <div className="ad-columns">
              {COLUMNS.map((column) => (
                <article className="ad-column" key={column.title}>
                  <h3>{column.title}</h3>
                  <ul className="ad-lines">
                    {column.lines.map((line) => (
                      <li className="ad-line" key={line}>{line}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <div className="ad-handoff-note">
              <p className="eyebrow">HOW YOU HEAR ABOUT IT</p>
              <p>
                Every send, hold, reply and meeting lands in the deal feed and the notification
                bell. Interested replies and confirmed meetings also reach your email, with a
                reminder the morning of the call. Each check writes to an append-only log: what it
                looked at, what it queued, what it skipped and why, what it sent. Test it safely
                first: redirect mode sends every email for a deal to your own inbox with the
                original recipient in the subject line.
              </p>
            </div>
          </div>
        </section>

        <section className="section ad-faq" aria-labelledby="agentdart-faq-title">
          <div className="shell faq-grid">
            <div className="faq-heading">
              <p className="eyebrow">QUESTIONS</p>
              <h2 id="agentdart-faq-title">Before you hand it over</h2>
            </div>
            <div className="faq-list">
              {FAQS.map((item, index) => (
                <details key={item.question} open={index === 0}>
                  <summary>{item.question}</summary>
                  <p>
                    {item.answer}
                    {index === 1 ? (
                      <>
                        {" "}<Link to="/help/email-connections">Read about email connections.</Link>
                      </>
                    ) : null}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="closing ad-close">
          <div className="shell">
            <p className="eyebrow">YOUR DEAL. YOUR PERMISSION.</p>
            <h2>Start with your own deal.</h2>
            <p>Set the goal. Choose drafts or direct sending. See every decision.</p>
            <div className="ad-close-actions">
              <Link
                className="button button-gold button-hero"
                to="/auth"
                search={{ mode: "signup" as const }}
                data-cta="agentdart"
              >
                Start my free trial <span aria-hidden="true">→</span>
              </Link>
              <a
                className="ad-demo-link"
                href="https://demo.capitaldart.com"
                target="_blank"
                rel="noopener"
              >
                Interactive demo <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
